import { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ref, get, push, set } from 'firebase/database';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../ui/Card';
import {
  WorkspacePageError,
  WorkspacePageLoader,
} from '../ui/Workspace';
import {
  FaArrowLeft,
  FaBookOpen,
  FaFileDownload,
  FaLink,
  FaPaperclip,
  FaVideo,
} from 'react-icons/fa';
import { formatCurrency } from '../../utils/formatCurrency';
import { lessonReaderUrlSegment, sortLessonsBySession } from '../../utils/lessonReader';
import { useBranches } from '../../hooks/useBranches';
import { resolveBranchRef } from '../../utils/branchResolver';
import {
  getYouTubeEmbedUrl,
  normalizeLessonAttachments,
  normalizeLessonVideos,
} from '../../utils/lessonMedia';

const CourseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { byId: branchesById, bySlug: branchesBySlug } = useBranches();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [loadError, setLoadError] = useState(null);
  /** Named `error` for enrollment/batch UI so HMR and legacy JSX never hit ReferenceError */
  const [error, setError] = useState(null);
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(null);
  const isAdmin = currentUser?.role === 'admin';
  const [enrollmentRequests, setEnrollmentRequests] = useState({});

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        const courseRef = ref(db, `courses/${id}`);
        const snapshot = await get(courseRef);

        if (snapshot.exists()) {
          setLoadError(null);
          // Ensure all required objects exist with default values
          const courseData = {
            minimumQualifications: {
              olRequired: false,
              alRequired: false,
              workExperience: 0,
            },
            batches: [],
            curriculum: [],
            fees: {
              fullCourseFee: 0,
              registrationFee: 0,
              numberOfInstallments: 1,
            },
            duration: {
              isOneDay: false,
              months: 0,
            },
            ...snapshot.val(),
          };
          setCourse({ id, ...courseData });
        } else {
          setLoadError('Course not found');
        }
      } catch (err) {
        console.error('Error fetching course:', err);
        setLoadError('Failed to load course details');
      } finally {
        setLoading(false);
      }
    };

    const fetchEnrollmentRequests = async () => {
      try {
        const requestsRef = ref(db, 'enrollmentRequests');
        const snapshot = await get(requestsRef);
        if (snapshot.exists()) {
          const requests = snapshot.val();
          // Filter requests for current user and course
          const userRequests = Object.values(requests).filter(
            (request) =>
              request.studentId === currentUser.uid &&
              request.courseId === id &&
              request.status === 'pending',
          );
          // Create a map of batch index to request
          const requestMap = {};
          userRequests.forEach((request) => {
            requestMap[request.batchId] = request;
          });
          setEnrollmentRequests(requestMap);
        }
      } catch (error) {
        console.error('Error fetching enrollment requests:', error);
      }
    };

    fetchCourse();
    if (currentUser?.role === 'student') {
      fetchEnrollmentRequests();
    }
  }, [id, currentUser]);

  const handleEnrollRequest = async (batchId) => {
    try {
      setEnrolling(true);
      setError(null);
      setEnrollmentSuccess(null);

      // Check if user is already enrolled or has pending request
      const enrollmentsRef = ref(db, 'enrollments');
      const enrollmentsSnapshot = await get(enrollmentsRef);
      const enrollments = enrollmentsSnapshot.val() || {};

      const existingEnrollment = Object.values(enrollments).find(
        (enrollment) =>
          enrollment.studentId === currentUser.uid &&
          enrollment.courseId === id &&
          enrollment.batchId === batchId,
      );

      if (existingEnrollment) {
        setError('You have already enrolled or have a pending request for this batch');
        return;
      }

      // Create enrollment request
      const enrollmentRequestRef = push(ref(db, 'enrollmentRequests'));
      await set(enrollmentRequestRef, {
        id: enrollmentRequestRef.key,
        studentId: currentUser.uid,
        courseId: id,
        batchId: batchId,
        status: 'pending',
        requestDate: new Date().toISOString(),
        studentName: `${currentUser.firstName} ${currentUser.lastName}`,
        courseName: course.name,
      });

      setEnrollmentSuccess(
        'Enrollment request submitted successfully! Waiting for approval.',
      );
    } catch (error) {
      console.error('Error submitting enrollment request:', error);
      setError('Failed to submit enrollment request. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };

  const sortedLessons = useMemo(
    () => sortLessonsBySession(Array.isArray(course?.lessons) ? course.lessons : []),
    [course?.lessons],
  );

  const sectionNav = useMemo(() => {
    if (!course) return [];
    const items = [
      { id: 'course-info', label: 'Course information' },
      { id: 'qualifications', label: 'Qualifications' },
    ];
    if (course.batches?.length) items.push({ id: 'batches', label: 'Available batches' });
    if (course.curriculum?.length) items.push({ id: 'curriculum', label: 'Curriculum' });
    if (course.introVideoUrl) items.push({ id: 'intro-video', label: 'Introduction video' });
    if (sortedLessons.length) items.push({ id: 'lessons', label: 'Lessons' });
    return items;
  }, [course, sortedLessons.length]);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return <WorkspacePageLoader />;
  }

  if (loadError || !course) {
    return (
      <WorkspacePageError>
        <p>{loadError || 'Course not found'}</p>
        <button
          type="button"
          onClick={() => navigate('/courses')}
          className="mt-4 rounded-md bg-pink-600 px-4 py-2 text-white hover:bg-pink-700"
        >
          Back to Courses
        </button>
      </WorkspacePageError>
    );
  }

  const lessonsList = Array.isArray(course.lessons) ? course.lessons : [];

  const sidebar = (
    <div className="space-y-4">
      <Card title="This course" className="shadow-sm">
        <div className="space-y-2 text-sm text-gray-600">
          <p className="font-semibold text-gray-900">{course.name}</p>
          <p>
            <span className="text-gray-500">Type:</span> {course.type}
          </p>
          <p>
            <span className="text-gray-500">Duration:</span>{' '}
            {course.duration?.isOneDay
              ? 'One day workshop'
              : `${course.duration?.months ?? '—'} months`}
          </p>
          <p>
            <span className="text-gray-500">Level:</span> {course.academicLevel || '—'}
          </p>
          <p>
            <span className="text-gray-500">Fee:</span>{' '}
            {formatCurrency(course.fees?.fullCourseFee)}
          </p>
          <p>
            <span className="text-gray-500">Sessions:</span>{' '}
            {sortedLessons.length ? `${sortedLessons.length} in catalog` : 'None yet'}
          </p>
          {course.status ? (
            <p className="pt-1">
              <span className="rounded-full bg-stone-100 px-2.5 py-0.5 text-xs font-medium capitalize text-stone-700">
                {course.status}
              </span>
            </p>
          ) : null}
        </div>
      </Card>

      {sectionNav.length > 0 ? (
        <Card title="On this page" className="shadow-sm">
          <nav
            className="no-scrollbar max-h-56 space-y-1 overflow-y-auto text-sm"
            aria-label="Page sections"
          >
            {sectionNav.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollToSection(item.id)}
                className="flex w-full rounded-lg px-2 py-1.5 text-left text-slate-600 transition hover:bg-stone-50 hover:text-pink-800"
              >
                {item.label}
              </button>
            ))}
          </nav>
        </Card>
      ) : null}

      {sortedLessons.length > 0 ? (
        <Card title="Open in lesson reader" className="shadow-sm">
          <ol className="no-scrollbar max-h-64 space-y-0.5 overflow-y-auto text-sm">
            {sortedLessons.map((lesson) => {
              const seg = lessonReaderUrlSegment(lessonsList, lesson);
              return (
                <li key={lesson.id != null ? String(lesson.id) : `${lesson.sessionNumber}-${lesson.title}`}>
                  <Link
                    to={`/courses/${id}/lessons/${seg}`}
                    className="flex items-center gap-2 rounded-lg px-2 py-2 text-slate-600 transition hover:bg-pink-50 hover:text-pink-900"
                  >
                    <FaBookOpen className="shrink-0 text-xs text-pink-500" aria-hidden />
                    <span className="line-clamp-2">
                      {lesson.sessionNumber != null ? `${lesson.sessionNumber}. ` : ''}
                      {lesson.title || 'Session'}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ol>
        </Card>
      ) : null}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 via-white to-pink-50/40 pb-16 pt-2">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-stone-200/80 pb-4">
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-pink-700"
          >
            <FaArrowLeft className="text-xs" />
            Back to courses
          </Link>
        </header>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[320px_minmax(0,1fr)] xl:items-start">
          <aside className="no-scrollbar order-2 space-y-4 xl:sticky xl:top-6 xl:order-1 xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto xl:pr-1">
            {sidebar}
          </aside>

          <div className="order-1 min-w-0 xl:order-2">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <h1 className="text-2xl font-semibold text-gray-900">{course.name}</h1>
              {isAdmin ? (
                <button
                  type="button"
                  onClick={() => navigate(`/courses/edit/${id}`)}
                  className="min-h-[44px] shrink-0 rounded-md bg-pink-600 px-4 py-2 text-white hover:bg-pink-700"
                >
                  Edit course
                </button>
              ) : null}
            </div>

            {course.imageUrl && (
              <div className="mb-6 overflow-hidden rounded-2xl border border-stone-200/80 shadow-sm">
                <img
                  src={course.imageUrl}
                  alt={course.name}
                  className="h-64 w-full object-cover sm:h-72"
                />
              </div>
            )}

            <div className="space-y-6 rounded-2xl border border-stone-200/90 bg-white p-6 shadow-sm">
              <div id="course-info" className="scroll-mt-28">
          <h2 className="text-xl font-semibold mb-4">Course Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-gray-600">Type: {course.type}</p>
              <p className="text-gray-600">
                Duration:{' '}
                {course.duration?.isOneDay
                  ? 'One Day Workshop'
                  : `${course.duration?.months} months`}
              </p>
              <p className="text-gray-600">Level: {course.academicLevel}</p>
            </div>
            <div>
              <p className="text-gray-600">
                Full Course Fee: {formatCurrency(course.fees?.fullCourseFee)}
              </p>
              <p className="text-gray-600">
                Registration Fee: {formatCurrency(course.fees?.registrationFee)}
              </p>
              <p className="text-gray-600">
                Installments: {course.fees?.numberOfInstallments}
              </p>
            </div>
          </div>
        </div>

        <div id="qualifications" className="scroll-mt-28">
          <h2 className="text-xl font-semibold mb-4">Minimum Qualifications</h2>
          <ul className="list-disc list-inside text-gray-600">
            {course.minimumQualifications.olRequired && (
              <li>GCE O/L Required</li>
            )}
            {course.minimumQualifications.alRequired && (
              <li>GCE A/L Required</li>
            )}
            {course.minimumQualifications.workExperience > 0 && (
              <li>
                {course.minimumQualifications.workExperience} months work
                experience required
              </li>
            )}
            {!course.minimumQualifications.olRequired &&
              !course.minimumQualifications.alRequired &&
              !course.minimumQualifications.workExperience && (
                <li>No specific qualifications required</li>
              )}
          </ul>
        </div>

        {course.batches && course.batches.length > 0 && (
          <div id="batches" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-4">Available Batches</h2>

            {error ? (
              <div className="mb-4 rounded-md bg-red-100 p-3 text-red-700">{error}</div>
            ) : null}

            {enrollmentSuccess && (
              <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">
                {enrollmentSuccess}
              </div>
            )}

            <div className="space-y-4">
              {course.batches.map((batch, index) => {
                const hasPendingRequest = enrollmentRequests[index];

                return (
                  <div key={index} className="border rounded-md p-4 bg-gray-50">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium mb-2">Batch {index + 1}</h3>
                        <p className="text-gray-600">
                          Start Date:{' '}
                          {batch.startDate
                            ? `${batch.startDate.date}/${batch.startDate.month}/${batch.startDate.year}`
                            : 'Not set'}
                        </p>
                        <p className="text-gray-600">
                          Branch:{' '}
                          {resolveBranchRef(batch, {
                            byId: branchesById,
                            bySlug: branchesBySlug,
                          })?.name || 'Not set'}
                        </p>
                        <p className="text-gray-600">
                          Maximum Students: {batch.maxStudents}
                        </p>
                      </div>

                      {currentUser?.role === 'student' && (
                        <button
                          onClick={() => handleEnrollRequest(index)}
                          disabled={enrolling || hasPendingRequest}
                          className="min-h-[44px] rounded-md bg-pink-600 px-6 py-2 text-white transition-colors duration-200 hover:bg-pink-700 
                                   disabled:bg-pink-300 
                                   flex items-center gap-2"
                        >
                          {enrolling ? (
                            <>
                              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                              <span>Processing...</span>
                            </>
                          ) : hasPendingRequest ? (
                            <>
                              <span>Request Pending</span>
                              <span className="text-xl">⌛</span>
                            </>
                          ) : (
                            <>
                              <span>Join Batch</span>
                              <span className="text-xl">→</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {course.curriculum && course.curriculum.length > 0 && (
          <div id="curriculum" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-4">Course Curriculum</h2>
            <div className="space-y-4">
              {course.curriculum.map((topic, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <h3 className="font-medium text-lg">
                    Topic {index + 1}: {topic.title}
                  </h3>
                  <p className="text-gray-600 mt-1">{topic.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {course.introVideoUrl && (
          <div id="intro-video" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-4">Introduction Video</h2>
            <div className="aspect-w-16 aspect-h-9">
              {getYouTubeEmbedUrl(course.introVideoUrl) ? (
                <iframe
                  src={getYouTubeEmbedUrl(course.introVideoUrl)}
                  title="Course Introduction"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full rounded-lg"
                ></iframe>
              ) : (
                <div className="bg-gray-100 rounded-lg p-4 text-center text-gray-600">
                  Invalid YouTube URL
                </div>
              )}
            </div>
          </div>
        )}

        {course.lessons && course.lessons.length > 0 && (
          <div id="lessons" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-4">Course Lessons</h2>
            <div className="space-y-4">
              {course.lessons.map((lesson, index) => (
                <div key={index} className="border rounded-lg p-4">
                  {lesson.coverImage && (
                    <img
                      src={lesson.coverImage}
                      alt={lesson.title}
                      className="w-full h-48 object-cover rounded-md mb-4"
                    />
                  )}
                  <div className="flex justify-between items-start gap-4">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium">
                        Lesson {index + 1}: {lesson.title}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {lesson.date
                          ? new Date(lesson.date).toLocaleDateString()
                          : 'Date TBC'}
                      </p>
                      <p className="text-gray-600 mt-1">{lesson.description}</p>
                      {lesson.assignment?.brief && (
                        <div className="mt-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                          <p className="font-medium text-gray-900">
                            {lesson.assignment.title || 'Assignment'}
                          </p>
                          <p className="mt-1">{lesson.assignment.brief}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-2 sm:flex-row sm:items-start">
                      <Link
                        to={`/courses/${id}/lessons/${
                          lesson.id != null && String(lesson.id).trim() !== ''
                            ? lesson.id
                            : String(index)
                        }`}
                        className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-pink-600 to-pink-500 px-3 py-2 text-sm font-medium text-white shadow-sm hover:from-pink-700 hover:to-pink-600"
                      >
                        <FaBookOpen className="text-sm opacity-90" />
                        Reader
                      </Link>
                    <div className="flex gap-3">
                      {(() => {
                        const vids = normalizeLessonVideos(lesson);
                        if (!vids.length) return null;
                        return (
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            {vids.map((url, videoIndex) => {
                              const embed = getYouTubeEmbedUrl(url);
                              return (
                                <span
                                  key={`${url}-${videoIndex}`}
                                  className="inline-flex items-center gap-1.5"
                                >
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="shrink-0 text-pink-700 hover:text-pink-900"
                                    title={`Open Video ${videoIndex + 1}`}
                                  >
                                    <FaVideo />
                                  </a>
                                  {embed ? (
                                    <button
                                      type="button"
                                      onClick={() => window.open(embed, '_blank')}
                                      className="max-w-full break-words text-sm text-pink-700 hover:text-pink-900"
                                    >
                                      Video {vids.length > 1 ? videoIndex + 1 : ''}
                                    </button>
                                  ) : null}
                                </span>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {normalizeLessonAttachments(lesson).length > 0 ? (
                        <span
                          className="inline-flex max-w-full items-center gap-1 text-sm text-gray-600"
                          title="Attachments in reader"
                        >
                          <FaPaperclip className="shrink-0" />
                          {normalizeLessonAttachments(lesson).length}
                        </span>
                      ) : null}
                      {lesson.assignmentUrl && (
                        <a
                          href={lesson.assignmentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 text-pink-700 hover:text-pink-900"
                          title="Assignment"
                        >
                          <FaFileDownload />
                        </a>
                      )}
                      {lesson.tuteUrl && (
                        <a
                          href={lesson.tuteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 text-pink-700 hover:text-pink-900"
                          title="Tutorial"
                        >
                          <FaLink />
                        </a>
                      )}
                    </div>
                    </div>
                  </div>
                </div>
              ))}
              {!course.lessons.length && (
                <p className="text-gray-500 italic text-center">
                  No lessons added yet
                </p>
              )}
            </div>
          </div>
        )}
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={() => navigate('/courses')}
                className="text-sm font-medium text-pink-700 hover:text-pink-900"
              >
                ← Back to courses
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetails;
